/**
 * Layer-0 bot brain, B1 (WORKBOARD §14): *where* to fight from. Pure.
 *
 * The action bot used to close to a range band and strafe — every fight a walk toward each other.
 * Here, with a target in sight, it scores ~25 candidate spots (person-scale rings round itself,
 * oriented on the target, plus where it stands) and fights from the best:
 *
 *   + sees the target from the spot (eye to eye, terrain and props)
 *   + is covered from the target while seeing it (peek cover), less for covered-and-blind
 *   + stands higher than the threats it knows of (person-heights)
 *   − how many known threats can see the spot
 *   − outside the range band, − travel time, − crowding a teammate, + staying put (no flicker)
 *
 * Every weight lives in PositionWeights (data — B4 evolves it). Fair: threats are only the enemies
 * the bot sees. Lengths are person lengths × k (the scale profile), so it plays the same at any k.
 */

import { digCover } from '../core/dig';
import type { PlanetState, PlayerState, GameState, Vec3, CoverPose, Dig } from '../types';
import { rifleEffectivePeople } from '../core/support';
import { vecAdd, vecScale, vecSub, vecDot, vecNormalize, vecLength, surfaceTangentFrame } from '../math';
import { dacos, dsin, dcos } from '../core/dmath';
import { hasLineOfSight, eyePointAt } from '../core/los';
import { getSurfaceRadius } from '../core/surface';
import { segmentCovered } from '../core/bullets';
import type { WalkObstacle } from '../core/surfaceWalk';
import type { PersonSet } from '../core/scaleProfile';
import { personPointOn, personLineOfSight, personSurfaceRadius } from '../core/fineTerrain';
import { coverPointsNear, coverBearing, terrainSpotsNear, shelter, propsCoverBody, coverAcrossOn, COVER_ACROSS } from '../core/coverMap';
import { collapseHazardAt } from '../core/collapseHazard';
import { coverSlotsNear } from '../core/coverSlots';
import { stanceEyeScale } from '../core/stance';
import { leanReachPeople } from '../core/lean';
import { inLane, FIRE_LANE } from './fireLanes';
import { COVER_POSE_SIZE } from '../constants';
import { PERSON_BODY_HEIGHT } from '../core/scaleProfile';

export interface PositionWeights {
  /** Seeing the target from the spot. */
  los: number;
  /** Covered from the target while seeing it (peek cover); a third of it for covered and blind. */
  cover: number;
  /** Per person-height of ground over the known threats (clamped ±4). */
  height: number;
  /** Per known threat that can see the spot. */
  exposure: number;
  /** Per person-length outside [rangeMin, rangeMax]. */
  range: number;
  /** Per second of walking to the spot. */
  travel: number;
  /** Per teammate within 1.5 person-lengths. */
  crowd: number;
  /** Staying on (or near) the spot already chosen. */
  stay: number;
  /** The range band, in person-lengths (× k). */
  rangeMin: number;
  rangeMax: number;
}

export const DEFAULT_POSITION_WEIGHTS: PositionWeights = {
  los: 3, cover: 2, height: 0.5, exposure: 1.5, range: 0.25, travel: 0.6, crowd: 1, stay: 0.4, rangeMin: 6, rangeMax: 16,
};

/** Ring radii (person-lengths) and bearings sampled round the bot. */
const RINGS = [2, 4, 7];
const BEARINGS = 8;

export interface SpotChoice {
  dir: Vec3;
  score: number;
  sees: boolean;
  covered: boolean;
  exposure: number;
  heightAdv: number;
}

function arcOn(a: Vec3, b: Vec3, radius: number): number {
  return dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b))))) * radius;
}

function bodyPoint(planet: PlanetState, dir: Vec3, ps: PersonSet): Vec3 {
  return personPointOn(planet, dir, ps, ps.hitboxCenter);
}

export function chooseFightSpot(args: {
  state: Pick<GameState, 'planets' | 'rocks' | 'flora' | 'structures' | 'config'>;
  unit: PlayerState;
  ps: PersonSet;
  target: PlayerState;
  threats: readonly Pick<PlayerState, 'alive' | 'planetIndex' | 'surfaceDir'>[];   // K1: as known at the last look, not the true units
  teammates: readonly PlayerState[];
  obstacles: readonly WalkObstacle[];
  current: Vec3 | null;
  weights?: PositionWeights;
}): SpotChoice | null {
  const { state, unit, ps, target, threats, teammates, obstacles, current } = args;
  const w = args.weights ?? DEFAULT_POSITION_WEIGHTS;
  const planet = state.planets[unit.planetIndex];
  if (!planet || target.planetIndex !== unit.planetIndex) return null;
  const R = planet.radius;
  const k = ps.k;
  const n = vecNormalize(unit.surfaceDir);
  const targetEye = personPointOn(planet, target.surfaceDir, ps, ps.losEyeHeight);
  const threatEyes = threats.filter(t => t.alive && t.planetIndex === unit.planetIndex).map(t => ({ eye: personPointOn(planet, t.surfaceDir, ps, ps.losEyeHeight), ground: personSurfaceRadius(planet, vecNormalize(t.surfaceDir), ps) }));
  const threatGround = threatEyes.length ? threatEyes.reduce((a, t) => a + t.ground, 0) / threatEyes.length : personSurfaceRadius(planet, vecNormalize(target.surfaceDir), ps);
  const cache = new Map<number, WalkObstacle[]>([[unit.planetIndex, obstacles as WalkObstacle[]]]);

  // Candidates: where it stands, then rings oriented on the target
  const toTarget = vecSub(vecNormalize(target.surfaceDir), vecScale(n, vecDot(vecNormalize(target.surfaceDir), n)));
  const fwd = vecLength(toTarget) > 1e-9 ? vecNormalize(toTarget) : surfaceTangentFrame(n).tx;
  const side = vecNormalize({ x: n.y * fwd.z - n.z * fwd.y, y: n.z * fwd.x - n.x * fwd.z, z: n.x * fwd.y - n.y * fwd.x });
  const spots: Vec3[] = [n];
  for (const r of RINGS) {
    const th = (r * k) / R;
    for (let i = 0; i < BEARINGS; i++) {
      const a = (i / BEARINGS) * 2 * Math.PI;
      const along = vecAdd(vecScale(fwd, dcos(a)), vecScale(side, dsin(a)));
      spots.push(vecNormalize(vecAdd(vecScale(n, dcos(th)), vecScale(along, dsin(th)))));
    }
  }

  let best: SpotChoice | null = null;
  for (const dir of spots) {
    // Not inside a rock / trunk / structure, nor in a building coming down (72.3 step 7)
    if (obstacles.some(o => arcOn(dir, o.dir, R) < o.radius + ps.unitRadius) || collapseHazardAt(state, unit.planetIndex, dir)) continue;
    const eye = personPointOn(planet, dir, ps, ps.losEyeHeight);
    const body = bodyPoint(planet, dir, ps);
    const sees = personLineOfSight(eye, targetEye, state) && !segmentCovered(eye, targetEye, state, cache, ps.coverSampleStep);
    const bodySeen = personLineOfSight(targetEye, body, state) && !segmentCovered(targetEye, body, state, cache, ps.coverSampleStep);
    const covered = !bodySeen;
    let exposure = 0;
    for (const t of threatEyes) if (personLineOfSight(t.eye, body, state) && !segmentCovered(t.eye, body, state, cache, ps.coverSampleStep)) exposure++;
    const ground = personSurfaceRadius(planet, dir, ps);
    const heightAdv = Math.max(-4, Math.min(4, (ground - threatGround) / (ps.eyeHeight || k)));
    const d = arcOn(dir, target.surfaceDir, R) / k;
    const rangeErr = Math.max(0, w.rangeMin - d, d - w.rangeMax);
    const travel = arcOn(dir, n, R) / ps.walkSpeed;
    const crowd = teammates.filter(t => t.alive && t.id !== unit.id && t.planetIndex === unit.planetIndex && arcOn(t.surfaceDir, dir, R) < 1.5 * k).length;
    const stay = current && arcOn(current, dir, R) < 0.75 * k ? 1 : 0;
    const score = w.los * (sees ? 1 : 0) + w.cover * (covered ? (sees ? 1 : 1 / 3) : 0) + w.height * heightAdv
      - w.exposure * exposure - w.range * rangeErr - w.travel * travel - w.crowd * crowd + w.stay * stay;
    if (!best || score > best.score) best = { dir, score, sees, covered, exposure, heightAdv };
  }
  return best;
}

/** What a kill looked like (for the harness): range and the shooter's height advantage in person-lengths, and whether its body was hidden from the victim. */
export function engagementOf(state: GameState, shooter: PlayerState, victim: PlayerState, ps: PersonSet): { range: number; heightAdv: number; covered: boolean } | null {
  if (shooter.planetIndex !== victim.planetIndex || shooter.airborne || victim.airborne) return null;
  const planet = state.planets[shooter.planetIndex];
  const range = arcOn(shooter.surfaceDir, victim.surfaceDir, planet.radius) / ps.k;
  const heightAdv = (personSurfaceRadius(planet, vecNormalize(shooter.surfaceDir), ps) - personSurfaceRadius(planet, vecNormalize(victim.surfaceDir), ps)) / ps.k;
  const victimEye = personPointOn(planet, victim.surfaceDir, ps, ps.losEyeHeight);
  const body = bodyPoint(planet, shooter.surfaceDir, ps);
  const covered = !personLineOfSight(victimEye, body, state) || segmentCovered(victimEye, body, state, undefined, ps.coverSampleStep);
  return { range, heightAdv, covered };
}

/**
 * B2 hold: the spot inside a point's circle (the centre and a ring at 0.55 of the radius) that the
 * fewest known threats can see; ties keep the current spot, then the centre. Inside the circle,
 * so standing there still holds the point.
 */
export function chooseHoldSpot(args: {
  state: Pick<GameState, 'planets' | 'rocks' | 'flora' | 'structures' | 'config'>;
  planetIndex: number;
  site: Vec3;
  captureRadius: number;
  ps: PersonSet;
  threats: readonly Pick<PlayerState, 'alive' | 'planetIndex' | 'surfaceDir'>[];   // K1: as known at the last look, not the true units
  obstacles: readonly WalkObstacle[];
  current: Vec3 | null;
}): Vec3 {
  const { state, planetIndex, site, captureRadius, ps, threats, obstacles, current } = args;
  const planet = state.planets[planetIndex];
  const R = planet.radius;
  const c = vecNormalize(site);
  const { tx, ty } = surfaceTangentFrame(c);
  const spots: Vec3[] = [c];
  const th = (0.55 * captureRadius) / R;
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * 2 * Math.PI;
    spots.push(vecNormalize(vecAdd(vecScale(c, dcos(th)), vecScale(vecAdd(vecScale(tx, dcos(a)), vecScale(ty, dsin(a))), dsin(th)))));
  }
  const eyes = threats.filter(t => t.alive && t.planetIndex === planetIndex).map(t => personPointOn(planet, t.surfaceDir, ps, ps.losEyeHeight));
  const cache = new Map<number, WalkObstacle[]>([[planetIndex, obstacles as WalkObstacle[]]]);
  let best: { dir: Vec3; score: number } | null = null;
  for (const dir of spots) {
    if (obstacles.some(o => arcOn(dir, o.dir, R) < o.radius + ps.unitRadius) || collapseHazardAt(state, planetIndex, dir)) continue;   // (72.3 step 7: none in a building coming down)
    const body = bodyPoint(planet, dir, ps);
    let exposure = 0;
    for (const e of eyes) if (personLineOfSight(e, body, state) && !segmentCovered(e, body, state, cache, ps.coverSampleStep)) exposure++;
    const stay = current && arcOn(current, dir, R) < 0.5 * ps.k ? 0.5 : 0;
    const score = -exposure + stay - 0.01 * arcOn(dir, c, R) / ps.k;
    if (!best || score > best.score) best = { dir, score };
  }
  return best?.dir ?? c;
}

/** B2 retreat: B1's scorer with weights that want to be unseen and further off. */
export const RETREAT_WEIGHTS: PositionWeights = {
  los: 0, cover: 3, height: 0.2, exposure: 3, range: 0.3, travel: 0.3, crowd: 0.5, stay: 0.4, rangeMin: 14, rangeMax: 40,
};

/**
 * B3 defence escort: an overwatch spot round a point — on rings at 5 and 8 person-lengths, the spot
 * that sees the point (eye to the centre) and that the fewest known threats can see; ties keep the
 * current spot. Facing nothing in particular when no threat is known (the point is the watch).
 */
export function chooseOverwatchSpot(args: {
  state: Pick<GameState, 'planets' | 'rocks' | 'flora' | 'structures' | 'config'>;
  planetIndex: number;
  site: Vec3;
  ps: PersonSet;
  threats: readonly Pick<PlayerState, 'alive' | 'planetIndex' | 'surfaceDir'>[];   // K1: as known at the last look, not the true units
  obstacles: readonly WalkObstacle[];
  current: Vec3 | null;
}): Vec3 {
  const { state, planetIndex, site, ps, threats, obstacles, current } = args;
  const planet = state.planets[planetIndex];
  const R = planet.radius;
  const c = vecNormalize(site);
  const { tx, ty } = surfaceTangentFrame(c);
  const target = bodyPoint(planet, c, ps);
  const eyes = threats.filter(t => t.alive && t.planetIndex === planetIndex).map(t => personPointOn(planet, t.surfaceDir, ps, ps.losEyeHeight));
  const cache = new Map<number, WalkObstacle[]>([[planetIndex, obstacles as WalkObstacle[]]]);
  let best: { dir: Vec3; score: number } | null = null;
  for (const ring of [5, 8]) {
    const th = (ring * ps.k) / R;
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * 2 * Math.PI;
      const dir = vecNormalize(vecAdd(vecScale(c, dcos(th)), vecScale(vecAdd(vecScale(tx, dcos(a)), vecScale(ty, dsin(a))), dsin(th))));
      if (obstacles.some(o => arcOn(dir, o.dir, R) < o.radius + ps.unitRadius) || collapseHazardAt(state, planetIndex, dir)) continue;   // (72.3 step 7)
      const eye = personPointOn(planet, dir, ps, ps.losEyeHeight);
      const watches = personLineOfSight(eye, target, state) && !segmentCovered(eye, target, state, cache, ps.coverSampleStep);
      const body = bodyPoint(planet, dir, ps);
      let exposure = 0;
      for (const e of eyes) if (personLineOfSight(e, body, state) && !segmentCovered(e, body, state, cache, ps.coverSampleStep)) exposure++;
      const stay = current && arcOn(current, dir, R) < 0.75 * ps.k ? 0.5 : 0;
      const score = (watches ? 2 : 0) - 1.5 * exposure + stay;
      if (!best || score > best.score) best = { dir, score };
    }
  }
  return best?.dir ?? c;
}


/** 68.15: how far round the place a squad takes its posts (person heights), and how far apart two posts stand (widths). */
export const HOLD_POST_RADIUS_PEOPLE = 6;
export const HOLD_POST_SPACING_WIDTHS = 2.5;
/** 66.7 (b): a member keeps its post unless a free one scores this much better (a tier: covered-and-seeing over blind cover). */
export const HOLD_POST_KEEP = 0.9;
/** 66.7 (coverMoves): at a capture point held from cover, a post whose eye sees the point's circle scores this much more. */
export const HOLD_WATCHES_PLACE = 0.75;
/** 66.7 (coverMoves): a holding squad keeps its posts until the threat's bearing from the place swings by more than this (rad) — attackers on the move no longer send it walking between posts. */
export const HOLD_FACE_ARC = Math.PI / 6;
/** 66.7 (coverMoves): threats spread wider than this (rad) are a fan — the squad's posts together against its middle — and the ground's spots that hide from that middle score this much more. */
export const HOLD_FAN_ARC = Math.PI / 3;
export const HOLD_ARC_SPOT = 0.75;
/** 66.7 (coverMoves): a squad holding a place (not a capture point) takes its posts among the cover this far round it (person heights). */
export const HOLD_COVER_AREA_PEOPLE = 10;
/** 66.7 (coverMoves): what a post loses for each placed mate's line of fire it would stand in, or each placed mate in its own. */
export const HOLD_LANE_PENALTY = 1.5;

/** The angle between the bearings from `place` to `a` and to `b` (surface directions or world points' directions; 0 when either is the place). */
export function bearingSwing(place: Vec3, a: Vec3, b: Vec3): number {
  const p = vecNormalize(place);
  const ta = vecSub(vecNormalize(a), vecScale(p, vecDot(vecNormalize(a), p))), tb = vecSub(vecNormalize(b), vecScale(p, vecDot(vecNormalize(b), p)));
  if (vecLength(ta) < 1e-12 || vecLength(tb) < 1e-12) return 0;
  return dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(ta), vecNormalize(tb)))));
}

/**
 * 68.15 (the owner, Oct 10, commanding from the diorama: "Most of the time I'm trying to tell them to fire from a treeline or
 * hold a series of rocks on a hill and the selection thing will send them to the bottom of the hill"): a squad sent to a
 * place (go here, hold here at a position) takes posts **at that place** — not formation slots that slide downhill, not a
 * nav point at the foot of the slope. Candidates: a spot behind each obstacle within HOLD_POST_RADIUS_PEOPLE of the place
 * (a rock, a trunk of the treeline, a ruin — on its far side from `facing`), and the open ground round the place. Each is
 * scored on what a defender wants: the body covered from the threat (a prop, or the ground at a crest) **and** the eye
 * seeing it (they fire from there); else behind a prop though blind (holding the rocks); else seeing in the open (the
 * crest, a firing line); dead ground behind a crest last — and the ground's height over the place, and the distance.
 * Members (in id order) take the best free post, two and a half person widths apart. `facing`: where the threat is (a
 * point on the planet, world space) — the known enemy, else straight ahead. Pure and deterministic.
 */
export function chooseHoldPosts(args: {
  state: Pick<GameState, 'planets' | 'rocks' | 'flora' | 'structures' | 'config'>;
  planetIndex: number;
  place: Vec3;
  facing: Vec3;
  ps: PersonSet;
  obstacles: readonly WalkObstacle[];
  members: ReadonlyArray<{ id: number }>;
  /** How far from the place a post may stand (u); default HOLD_POST_RADIUS_PEOPLE person heights (68.9: a capture circle's inside, a bound's cover). */
  reach?: number;
  /** 66.7 (b): each member's post as it stands — kept unless a free one is better by more than HOLD_POST_KEEP (no churn when the picture or the squad changes). */
  current?: ReadonlyMap<number, Vec3>;
}): Map<number, Vec3> {
  const { state, planetIndex, place, facing, ps, obstacles, members } = args;
  const planet = state.planets[planetIndex];
  const R = planet.radius;
  const c = vecNormalize(place);
  const person = ps.hitboxCenter * 2;   // (a person's height in u, near enough for distances)
  const reach = args.reach ?? HOLD_POST_RADIUS_PEOPLE * person;
  const threatDir = vecNormalize(vecSub(facing, planet.position));
  const threatEye = personPointOn(planet, threatDir, ps, ps.losEyeHeight);
  const groundAtPlace = personSurfaceRadius(planet, c, ps);
  const cands: Vec3[] = [c];
  // (66.7 b: the posts held now are candidates too, so a member can keep its own)
  for (const d of args.current?.values() ?? []) if (arcOn(d, c, R) <= reach) cands.push(vecNormalize(d));
  // 66.8: the digs in reach, half dug or more — a post the ground was made for (its digger keeps it as any holder its post)
  for (const dg of planet.digs ?? []) if (dg.done >= 0.5 && arcOn(dg.dir, c, R) <= reach) cands.push(vecNormalize(dg.dir));
  // behind each obstacle near the place, on its far side from the threat (findCover's rule)
  for (const o of obstacles) {
    if (arcOn(o.dir, c, R) > reach) continue;
    const away = vecSub(vecNormalize(o.dir), threatDir);
    const tang = vecSub(away, vecScale(o.dir, vecDot(away, o.dir)));
    if (vecLength(tang) < 1e-9) continue;
    // (just past the walk's own clearance round it — its body and half again — so the walk reaches the post and does not
    // circle the rock: a spot inside that ring is steered round forever)
    const th = (o.radius + 1.5 * ps.unitRadius + 0.1 * ps.k) / R;
    cands.push(vecNormalize(vecAdd(vecScale(o.dir, dcos(th)), vecScale(vecNormalize(tang), dsin(th)))));
  }
  // the open ground round the place: rings at a third and two thirds of the reach
  const { tx, ty } = surfaceTangentFrame(c);
  for (const f of [1 / 3, 2 / 3]) for (let i = 0; i < 8; i++) {
    const a = (i / 8) * 2 * Math.PI, th = (f * reach) / R;
    cands.push(vecNormalize(vecAdd(vecScale(c, dcos(th)), vecScale(vecAdd(vecScale(tx, dcos(a)), vecScale(ty, dsin(a))), dsin(th)))));
  }
  const cache = new Map<number, WalkObstacle[]>([[planetIndex, obstacles as WalkObstacle[]]]);
  const scored = cands
    // 66.7 (b): no post inside the walk's own clearance round anything (its body and half again — the steer's ring): the
    // walk could never stand there, and circled the rock round it, in and out of its cover (the spin the owner saw)
    .filter(d => !obstacles.some(o => arcOn(d, o.dir, R) < o.radius + 1.5 * ps.unitRadius + 0.05 * ps.k) && !collapseHazardAt(state, planetIndex, d))   // (72.3 step 7: none in a building coming down)
    .map((d, i) => {
      const body = bodyPoint(planet, d, ps);
      const eye = personPointOn(planet, d, ps, ps.losEyeHeight);
      const byGround = !personLineOfSight(threatEye, body, state);
      const byProp = propsCoverBody(state, threatEye, body, d, ps.hitboxRadius, cache, ps.coverSampleStep);   // (at a rock whatever the ground does; 70.3: across the body)
      const sees = personLineOfSight(eye, threatEye, state);
      const height = Math.max(-4, Math.min(4, (personSurfaceRadius(planet, d, ps) - groundAtPlace) / person));
      // covered and seeing (peek cover) best; then behind a prop though blind (holding the rocks, the trunks — the owner's
      // case); then seeing in the open (a crest, a firing line); dead ground behind the crest last
      const score = ((byGround || byProp) && sees ? 3 : byProp ? 2 : sees ? 1 : byGround ? 0.5 : 0) + 0.3 * height - 0.05 * arcOn(d, c, R) / person;
      return { d, score, i };
    })
    .sort((a, b) => b.score - a.score || a.i - b.i);
  const posts = new Map<number, Vec3>();
  const taken: Vec3[] = [];
  const spacing = HOLD_POST_SPACING_WIDTHS * 2 * ps.unitRadius;
  const ordered = [...members].sort((a, b) => a.id - b.id);
  // 66.7 (b), hysteresis: first, each member keeps the post it holds while that is still a post here and nothing free
  // beats it by more than HOLD_POST_KEEP (a teammate falling, the threat shifting — no walking between near-equal posts)
  const current = args.current;
  if (current) {
    const same = (a: Vec3, b: Vec3) => arcOn(a, b, R) < 0.1 * person;
    const free = (x: { d: Vec3 }) => taken.every(t => arcOn(t, x.d, R) >= spacing);
    for (const m of ordered) {
      const cur = current.get(m.id);
      // 66.8: dug in — its dig kept when the contact it faces has come inside the rifle's reach before the dig's front
      if (cur && free({ d: cur }) && dugIn(planet, cur, args.facing, ps)) { posts.set(m.id, vecNormalize(cur)); taken.push(vecNormalize(cur)); continue; }
      const mine = cur ? scored.find(x => same(x.d, cur)) : undefined;
      if (!mine || !free(mine)) continue;
      // the best free post that is no other member's own (theirs to keep)
      const heldByOthers = ordered.filter(q => q.id !== m.id).map(q => current.get(q.id)).filter((d): d is Vec3 => !!d);
      const best = scored.find(x => free(x) && !heldByOthers.some(d => same(x.d, d)));
      if (best && best.score > mine.score + HOLD_POST_KEEP) continue;
      posts.set(m.id, mine.d);
      taken.push(mine.d);
    }
  }
  for (const m of ordered) {
    if (posts.has(m.id)) continue;
    const pick = scored.find(x => taken.every(t => arcOn(t, x.d, R) >= spacing)) ?? scored[0];
    if (!pick) break;
    posts.set(m.id, pick.d);
    taken.push(pick.d);
  }
  return posts;
}

/**
 * 66.7 (coverMoves) — a squad holding a place from cover, each member against its own threat (the owner: *"take cover from
 * enemy direction when following hold position orders"*; the held-point assault showed one facing cannot hold a fan of
 * attackers: a holder fighting from a post sheltered from one bearing was covered 27 % of the time). `threatOf` gives each
 * member the world point its post shelters from — its target, the attacker nearest it, else its bearing in a fan round the
 * enemy's side. Candidates as chooseHoldPosts' (the place, the posts held, each prop's far side from each threat, the cover
 * map's points and the ground's spots against each threat, rings), scored per threat by the same tiers (covered and seeing;
 * a reverse slope a step below a crest; behind a prop; seeing; dead ground), and each member takes the best free one in its
 * own threat's ranking — keeping the post it holds unless a free one beats it by HOLD_POST_KEEP. With `keep` (a capture
 * point), the member on foot nearest the place keeps the circle first (69.91: it was the last member, which left the circle
 * empty while that member was held elsewhere — story-8 t0's q0 at p5, its keeper caught 63 people off at the motor pool
 * while the two who had come stood their posts at 3.6 and 5.1 people, the circle 2.73), and a post that watches the circle
 * scores HOLD_WATCHES_PLACE more.
 */
export function chooseCoverPosts(args: {
  state: Pick<GameState, 'planets' | 'rocks' | 'flora' | 'structures' | 'config'>;
  planetIndex: number;
  place: Vec3;
  ps: PersonSet;
  obstacles: readonly WalkObstacle[];
  /** (69.91: with `keep`, where each is and whether it is seated or aboard — the keeper is the nearest on foot) */
  members: ReadonlyArray<{ id: number; surfaceDir?: Vec3; mount?: unknown; boarded?: unknown }>;
  threatOf: ReadonlyMap<number, Vec3>;
  reach?: number;
  keep?: number;
  current?: ReadonlyMap<number, Vec3>;
  /** 66.22: the leans this decision may ask (absent: POSTS_LEAN_BUDGET). */
  leanBudget?: number;
}): Map<number, Vec3> {
  const { state, planetIndex, place, ps, obstacles, members, threatOf } = args;
  const planet = state.planets[planetIndex];
  const R = planet.radius;
  const c = vecNormalize(place);
  const person = ps.hitboxCenter * 2;
  // (a place the squad was sent to hold — a rock field, a stand, a crest: the owner, "I'd think that we could let the bot
  // choose the optimal cover in that area" — its cover across HOLD_COVER_AREA_PEOPLE; a capture point's round its circle)
  const reach = args.reach ?? (args.keep !== undefined ? HOLD_POST_RADIUS_PEOPLE : HOLD_COVER_AREA_PEOPLE) * person;
  const ordered = [...members].sort((a, b) => a.id - b.id);
  // the distinct threats (members sharing a target share its ranking), in member order
  const keyOf = (p: Vec3) => `${Math.round(p.x * 1e4)},${Math.round(p.y * 1e4)},${Math.round(p.z * 1e4)}`;
  let threats: Array<{ key: string; eye: Vec3; dir: Vec3 }> = [];
  const threatIndex = new Map<number, number>();
  for (const m of ordered) {
    const t = threatOf.get(m.id);
    if (!t) continue;
    const key = keyOf(t);
    let i = threats.findIndex(x => x.key === key);
    if (i < 0) {
      const dir = vecNormalize(vecSub(t, planet.position));
      threats.push({ key, dir, eye: personPointOn(planet, dir, ps, ps.losEyeHeight) });
      i = threats.length - 1;
    }
    threatIndex.set(m.id, i);
  }
  if (threats.length === 0) return new Map();
  // a fan — threats across more than HOLD_FAN_ARC (several attackers, or none known: the fan round the enemy's side) — and
  // the squad takes its posts together against the fan's middle, the ground's reverse slopes and dead ground preferred:
  // they shelter from an arc where a rock shelters from a bearing (the held-point assault: posts at rocks, each against its
  // own threat, were still seen by the rest of a spread assault two times in three)
  let spread = 0;
  for (let i = 0; i < threats.length; i++) for (let j = i + 1; j < threats.length; j++) spread = Math.max(spread, bearingSwing(c, threats[i].dir, threats[j].dir));
  const fan = spread > HOLD_FAN_ARC;
  if (fan) {
    let sx = 0, sy = 0, sz = 0, range = 0;
    for (const t of threats) { sx += t.dir.x; sy += t.dir.y; sz += t.dir.z; range += arcOn(t.dir, c, R); }
    range /= threats.length;
    const mid = vecSub(vecNormalize({ x: sx, y: sy, z: sz }), vecScale(c, vecDot(vecNormalize({ x: sx, y: sy, z: sz }), c)));
    if (vecLength(mid) > 1e-12) {
      const th = range / R;
      const dir = vecNormalize(vecAdd(vecScale(c, dcos(th)), vecScale(vecNormalize(mid), dsin(th))));
      threats = [{ key: 'fan', dir, eye: personPointOn(planet, dir, ps, ps.losEyeHeight) }];
      for (const m of ordered) threatIndex.set(m.id, 0);
    }
  }
  // 66.22 (lane F's profile at the owner's URL: one leader's posts were 89 % of a 3.5 s tick): the props near the place —
  // every standing-room test here (a prop's far side, a candidate's room, a lean's step aside) is about a point within
  // `reach` × 1.05 of it, a step (LEAN_STEPS_PEOPLE's largest) more for a lean, so a prop farther than that and its own
  // clearance cannot answer one (the triangle inequality on the sphere): the same answers, from the few props in reach, not
  // the planet's ~5,000 scanned per candidate and per lean step. The long lines (segmentCovered, through `cache`) keep them all
  const nearR = reach * 1.05 + (LEAN_STEPS_PEOPLE[LEAN_STEPS_PEOPLE.length - 1] + 0.5) * PERSON_BODY_HEIGHT * ps.k + 2 * ps.unitRadius + 0.1 * ps.k;
  const near = obstacles.filter(o => arcOn(o.dir, c, R) <= nearR + o.radius);
  const arcSpots = new Set<Vec3>();   // (in a fan: the ground's spots that hide from its middle)
  // candidates, shared by every threat
  const cands: Vec3[] = [c];
  for (const d of args.current?.values() ?? []) if (arcOn(d, c, R) <= reach) cands.push(vecNormalize(d));
  // 66.8: the digs in reach, half dug or more — a post the ground was made for (its digger keeps it as any holder its post)
  for (const dg of planet.digs ?? []) if (dg.done >= 0.5 && arcOn(dg.dir, c, R) <= reach) cands.push(vecNormalize(dg.dir));
  const stepUp = threats.map(() => new Set<Vec3>());
  for (const o of near) {
    if (arcOn(o.dir, c, R) > reach) continue;
    const th = (o.radius + 1.5 * ps.unitRadius + 0.1 * ps.k) / R;
    for (const t of threats) {
      const away = vecSub(vecNormalize(o.dir), t.dir);
      const tang = vecSub(away, vecScale(o.dir, vecDot(away, o.dir)));
      if (vecLength(tang) < 1e-9) continue;
      cands.push(vecNormalize(vecAdd(vecScale(o.dir, dcos(th)), vecScale(vecNormalize(tang), dsin(th)))));
    }
  }
  for (const p of coverPointsNear(state, planetIndex, c, reach)) if (!p.concealment && threats.some(t => p.shelter[coverBearing(p, t.eye)] !== null)) cands.push(vecNormalize(p.dir));
  threats.forEach((t, ti) => {
    for (const s of terrainSpotsNear(state, planetIndex, c, reach, t.eye)) {
      if (s.hides === null && s.kind !== 'crest') continue;
      const d = vecNormalize(s.dir);
      if (s.kind === 'reverseSlope' && s.crest) stepUp[ti].add(d);
      if (fan && s.hides !== null) arcSpots.add(d);
      cands.push(d);
    }
  });
  const { tx, ty } = surfaceTangentFrame(c);
  for (const f of [1 / 3, 2 / 3]) for (let i = 0; i < 8; i++) {
    const a = (i / 8) * 2 * Math.PI, th = (f * reach) / R;
    cands.push(vecNormalize(vecAdd(vecScale(c, dcos(th)), vecScale(vecAdd(vecScale(tx, dcos(a)), vecScale(ty, dsin(a))), dsin(th)))));
  }
  const cache = new Map<number, WalkObstacle[]>([[planetIndex, obstacles as WalkObstacle[]]]);
  const standable = cands.filter(d => arcOn(d, c, R) <= reach * 1.05 && !near.some(o => arcOn(d, o.dir, R) < o.radius + 1.5 * ps.unitRadius + 0.05 * ps.k) && !collapseHazardAt(state, planetIndex, d));   // (72.3 step 7)
  const groundAtPlace = personSurfaceRadius(planet, c, ps);
  const placeBody = bodyPoint(planet, c, ps);
  // the per-candidate parts that do not depend on the threat
  const base = standable.map(d => {
    const eye = personPointOn(planet, d, ps, ps.losEyeHeight);
    const height = Math.max(-4, Math.min(4, (personSurfaceRadius(planet, d, ps) - groundAtPlace) / person));
    const watches = args.keep !== undefined && personLineOfSight(eye, placeBody, state) && !segmentCovered(eye, placeBody, state, cache, ps.coverSampleStep) ? HOLD_WATCHES_PLACE : 0;
    return { d, eye, body: bodyPoint(planet, d, ps), extra: 0.3 * height - 0.05 * arcOn(d, c, R) / person + watches };
  });
  // each threat's lines to each candidate: hidden by the ground, by a prop, and whether it sees the threat
  const lines = threats.map(t => base.map(x => ({ byGround: !personLineOfSight(t.eye, x.body, state), byProp: propsCoverBody(state, t.eye, x.body, x.d, ps.hitboxRadius, cache, ps.coverSampleStep), sees: personLineOfSight(x.eye, t.eye, state) })));
  // (66.7, the owner: "aggressive cover … minimize exposure to enemy while dealing damage": behind a prop and blind, a post
  // with a lean that can fire — a step to a side whose gun clears to the threat — ranks above one that cannot.) 66.22: a
  // lean is up to ~14 sight lines, asked of every candidate behind a prop and blind to each threat — a settlement's props by a
  // fan of threats asked thousands (lane F: 16,746 lines in one leader's decision). Up to POSTS_LEAN_BUDGET a decision they
  // are all asked, as before; past it, those whose post would rank highest with a lean (its score at the lean's tier) first,
  // the rest ranked as without one
  const wantLean: Array<{ ti: number; i: number; s: number }> = [];
  lines.forEach((row, ti) => row.forEach((l, i) => { if (l.byProp && !l.sees) wantLean.push({ ti, i, s: base[i].extra + (arcSpots.has(base[i].d) ? HOLD_ARC_SPOT : 0) }); }));
  const leans = new Set<number>();
  for (const w of leansToAsk(wantLean, args.leanBudget ?? POSTS_LEAN_BUDGET)) if (leanSide(state, planet, base[w.i].d, threats[w.ti].dir, ps, near, leanReachPeople('edge')) !== null) leans.add(w.ti * base.length + w.i);
  const ranked = threats.map((t, ti) => base.map((x, i) => {
    const { byGround, byProp, sees } = lines[ti][i];
    const lean = leans.has(ti * base.length + i);
    const tier = (byGround || byProp) && sees ? 3 : lean ? 2.75 : byGround && stepUp[ti].has(x.d) ? 2.5 : byProp ? 2 : sees ? 1 : byGround ? 0.5 : 0;
    return { d: x.d, score: tier + x.extra + (arcSpots.has(x.d) ? HOLD_ARC_SPOT : 0), i };
  }).sort((a, b) => b.score - a.score || a.i - b.i));

  const posts = new Map<number, Vec3>();
  const taken: Vec3[] = [];
  const spacing = HOLD_POST_SPACING_WIDTHS * 2 * ps.unitRadius;
  const free = (d: Vec3) => taken.every(t => arcOn(t, d, R) >= spacing);
  const same = (a: Vec3, b: Vec3) => arcOn(a, b, R) < 0.1 * person;
  // 66.6's lanes: each placed post's line of fire to its threat; a post that would stand in one, or have one placed post in
  // its own, is penalised HOLD_LANE_PENALTY each — so a squad's posts fan across the threat's arc, not stack behind each other
  // (the 6 v 9 assault: holders standing their posts fired 17 % less with lanes on, as much as without cover with lanes off)
  const laneW = FIRE_LANE.widthPeople * ps.hitboxCenter * 2;
  const placed: Array<{ body: Vec3; eye: Vec3; threatEye: Vec3 }> = [];
  const assign = (id: number, d: Vec3) => {
    posts.set(id, d); taken.push(d);
    placed.push({ body: bodyPoint(planet, d, ps), eye: personPointOn(planet, d, ps, ps.losEyeHeight), threatEye: threats[threatIndex.get(id) ?? 0].eye });
  };
  const lanePenalty = (d: Vec3, ti: number): number => {
    if (placed.length === 0) return 0;
    const body = bodyPoint(planet, d, ps), eye = personPointOn(planet, d, ps, ps.losEyeHeight), tEye = threats[ti].eye;
    let pen = 0;
    for (const a of placed) {
      if (inLane(body, { owner: -1, from: a.eye, to: a.threatEye }, laneW)) pen += HOLD_LANE_PENALTY;
      if (inLane(a.body, { owner: -1, from: eye, to: tEye }, laneW)) pen += HOLD_LANE_PENALTY;
    }
    return pen;
  };
  const bestFree = (list: Array<{ d: Vec3; score: number }>, ti: number, notThese: readonly Vec3[]): { d: Vec3; s: number } | null => {
    let best: { d: Vec3; s: number } | null = null;
    for (const x of list) {
      if (!free(x.d) || notThese.some(d => same(x.d, d))) continue;
      if (best && x.score <= best.s) break;   // (ranked: nothing further down can win)
      const sc = x.score - lanePenalty(x.d, ti);
      if (!best || sc > best.s) best = { d: x.d, s: sc };
    }
    return best;
  };
  // the keeper (a capture point): the member on foot nearest the place (ties to the lower id), inside the circle; with no
  // member's place given, or none on foot, the last member (as before 69.91)
  if (args.keep !== undefined && ordered.length > 0) {
    let keeper = ordered[ordered.length - 1], best = Infinity;
    for (const m of ordered) {
      if (!m.surfaceDir || m.mount || m.boarded) continue;
      const d = arcOn(m.surfaceDir, c, R);
      if (d < best) { best = d; keeper = m; }
    }
    const inside = (d: Vec3) => arcOn(d, c, R) <= args.keep!;
    const list = ranked[threatIndex.get(keeper.id) ?? 0];
    const cur = args.current?.get(keeper.id);
    const pick = (cur && inside(cur) ? list.find(x => same(x.d, cur)) : undefined) ?? list.find(x => inside(x.d));
    const d = pick ? pick.d : c;
    assign(keeper.id, d);
  }
  // 66.8: dug in — a member whose post is its dig, with its threat come inside the rifle's reach before the dig's front,
  // keeps it whatever ranks higher (dugIn)
  for (const m of ordered) {
    const cur = args.current?.get(m.id), t = threatOf.get(m.id);
    if (posts.has(m.id) || !cur || !t || !free(cur) || !dugIn(planet, cur, t, ps)) continue;
    assign(m.id, vecNormalize(cur));
  }
  // each keeps the post it holds while it is still a candidate, free, and nothing free in its ranking beats it by HOLD_POST_KEEP
  // (the lanes counted on both: a post in a placed mate's line of fire, or with one in its own, holds its fire under 66.6)
  for (const m of ordered) {
    if (posts.has(m.id)) continue;
    const ti = threatIndex.get(m.id) ?? 0, list = ranked[ti];
    const cur = args.current?.get(m.id);
    const mine = cur ? list.find(x => same(x.d, cur)) : undefined;
    if (!mine || !free(mine.d)) continue;
    const others = ordered.filter(q => q.id !== m.id).map(q => args.current?.get(q.id)).filter((d): d is Vec3 => !!d);
    const best = bestFree(list, ti, others);
    if (best && best.s > mine.score - lanePenalty(mine.d, ti) + HOLD_POST_KEEP) continue;
    assign(m.id, mine.d);
  }
  for (const m of ordered) {
    if (posts.has(m.id)) continue;
    const ti = threatIndex.get(m.id) ?? 0, list = ranked[ti];
    const pick = bestFree(list, ti, []) ?? (list[0] ? { d: list[0].d, s: 0 } : null);
    if (!pick) continue;
    assign(m.id, pick.d);
  }
  return posts;
}

/**
 * 66.7 (coverMoves; the owner, playing it: *"for hold / cover the bots prefer to lie down behind cover, which is effective
 * but means they don't shoot much or see much"*): how a holder stands at its post against its threat (a world point) —
 * keeping the gun on it. Judged by the gun's line (the muzzle to the threat's body, as trigger discipline judges it), not the
 * eye's: an eye that clears low cover can have the gun below it blocked (the 6 v 9 assault: holders fired 15 % less).
 * Crouched where the crouched body is covered and the crouched gun clears; `rise` where the crouched body is covered and
 * only the standing gun clears (crouched while nothing is in sight, up to fire); standing where the cover hides the body
 * standing and the gun clears over it; prone only behind a lip a body is hidden by lying down alone; else crouched (tall
 * cover: it peeks round it). Chosen with the posts (a post decision), not on every look.
 */
export function postStance(state: Pick<GameState, 'planets' | 'rocks' | 'flora' | 'structures' | 'config'>, planetIndex: number, post: Vec3, threat: Vec3, ps: PersonSet): 'crouch' | 'stand' | 'prone' | 'rise' {
  const planet = state.planets[planetIndex];
  const tDir = vecNormalize(vecSub(threat, planet.position));
  const tEye = personPointOn(planet, tDir, ps, ps.losEyeHeight);
  const tBody = personPointOn(planet, tDir, ps, ps.hitboxCenter);
  const d = vecNormalize(post);
  const gunClears = (scale: number) => {
    const muzzle = personPointOn(planet, d, ps, ps.eyeHeight * scale);
    return personLineOfSight(muzzle, tBody, state) && !segmentCovered(muzzle, tBody, state, undefined, ps.coverSampleStep);
  };
  const covered = (st: 'stand' | 'crouch' | 'prone') => shelter(state, planetIndex, d, tEye, st).kind === 'covered';
  const crouchHidden = covered('crouch');
  if (crouchHidden && gunClears(stanceEyeScale('crouch'))) return 'crouch';
  // (hidden crouched and only the standing gun clears: down while nothing is in sight, up to fire — safer than standing)
  if (crouchHidden && gunClears(1)) return 'rise';
  if (covered('stand') && gunClears(1)) return 'stand';
  // (prone only behind the ground — a lip, a crest, a dip: a trunk hides a standing body's width, not a lying one's length)
  if (!crouchHidden && shelter(state, planetIndex, d, tEye, 'prone').by === 'ground') return 'prone';
  return 'crouch';
}

/**
 * 66.7: the side (1 right, −1 left, seen from `post` facing the threat at `threatDir`) a holder leans out to from behind its
 * cover, and how far it must step aside to fire — 0 when the pose's own lean clears (lean step 2: the eyes and the gun
 * aside by the pose's measured reach, the body at the post), else the smallest of LEAN_STEPS_PEOPLE whose spot stands clear
 * of every prop and whose gun clears to the threat's body — or null (nothing fires from here). `reach`: the pose's lean at the
 * eyes, person heights (lean.ts leanReachPeople; absent: no lean in place, a step only).
 */
export function leanSide(state: Pick<GameState, 'planets' | 'rocks' | 'flora' | 'structures' | 'config'>, planet: PlanetState, post: Vec3, threatDir: Vec3, ps: PersonSet, obstacles: readonly WalkObstacle[], reach?: number): { side: 1 | -1; step: number; at?: Vec3 } | null {
  const p = vecNormalize(post), t = vecNormalize(threatDir), R = planet.radius;
  const fwd = vecSub(t, vecScale(p, vecDot(t, p)));
  if (vecLength(fwd) < 1e-12) return null;
  const f = vecNormalize(fwd);
  const right = vecNormalize({ x: f.y * p.z - f.z * p.y, y: f.z * p.x - f.x * p.z, z: f.x * p.y - f.y * p.x });
  const body = personPointOn(planet, t, ps, ps.hitboxCenter);
  const person = PERSON_BODY_HEIGHT * ps.k;
  const clears = (muzzle: Vec3) => personLineOfSight(muzzle, body, state) && !segmentCovered(muzzle, body, state, undefined, ps.coverSampleStep);
  // the lean in place: the gun aside by the pose's reach (the body stays) — from the post, else from the post moved to the
  // cover's edge on that side (lane E's poses hold an edge: the body just inside it, the lean past it) while the body there
  // is still hidden from the threat
  if (reach !== undefined) {
    const tEye = personPointOn(planet, t, ps, ps.losEyeHeight);
    for (const shift of LEAN_EDGE_SHIFTS_PEOPLE) for (const side of [1, -1] as const) {
      const th = (shift * person) / R;
      const q = shift === 0 ? p : vecNormalize(vecAdd(vecScale(p, dcos(th)), vecScale(right, side * dsin(th))));
      if (shift > 0) {
        if (obstacles.some(o => arcOn(q, o.dir, R) < o.radius + 1.5 * ps.unitRadius)) continue;
        if (shelter(state, planet === state.planets[0] ? 0 : state.planets.indexOf(planet), q, tEye, 'crouch').kind !== 'covered') continue;   // (still hidden, down)
      }
      const m = personPointOn(planet, q, ps, ps.eyeHeight);
      if (clears(vecAdd(m, vecScale(right, side * reach * person)))) return { side, step: 0, ...(shift > 0 ? { at: q } : {}) };
    }
  }
  for (const step of LEAN_STEPS_PEOPLE) for (const side of [1, -1] as const) {
    const th = (step * person) / R;
    const d = vecNormalize(vecAdd(vecScale(p, dcos(th)), vecScale(right, side * dsin(th))));
    if (obstacles.some(o => arcOn(d, o.dir, R) < o.radius + 1.5 * ps.unitRadius)) continue;
    if (clears(personPointOn(planet, d, ps, ps.eyeHeight))) return { side, step };
  }
  return null;
}

/** 66.22: the leans (leanSide, up to ~14 sight lines each) a posts decision asks at most — past it, the posts that would rank highest with one. The owner's URL's decisions asked at most 56 with the brains' rate off; with lane F's 62.68 rate on, tick 6021 asks more, and its posts change (v124). */
export const POSTS_LEAN_BUDGET = 64;

/**
 * 66.22: which of a posts decision's wanted leans it asks — every one, in order, up to `budget`; past it, the `budget` whose
 * posts score highest at the lean's tier (`s`: the post's score less its tier — every wanted lean has the same tier), ties by
 * threat and then by candidate. The rest rank as without a lean (tier 2, behind a prop). Where it binds the posts can change:
 * a post past the budget that would have leaned ranks below one it would have beaten; the top-ranked keep their leans.
 */
export function leansToAsk<T extends { ti: number; i: number; s: number }>(want: readonly T[], budget: number): readonly T[] {
  if (want.length <= budget) return want;
  return [...want].sort((a, b) => b.s - a.s || a.ti - b.ti || a.i - b.i).slice(0, Math.max(0, budget));
}
/** 66.7 lean step 2: how far (person heights) a lean post may move toward the cover's edge, smallest first (0: where it is). */
export const LEAN_EDGE_SHIFTS_PEOPLE = [0, 0.1, 0.2, 0.3] as const;
/** 66.7: the side steps (person heights) a holder tries when its pose's lean cannot clear its cover, smallest first. */
export const LEAN_STEPS_PEOPLE = [0.3, 0.6, 1] as const;

/**
 * 66.8: the dig at a post (half dug or more, the post within half a person of its centre) when an enemy at `threat` (a world
 * point) has come inside the rifle's effective range of it, before the dig's front — its holder keeps it as its post then,
 * whatever else ranks higher (the ground was dug for this; a dig whose front is away from the contact is left as any post).
 */
export function dugIn(planet: PlanetState, post: Vec3, threat: Vec3, ps: PersonSet): Dig | null {
  if (!planet.digs?.length) return null;
  const R = planet.radius, p = vecNormalize(post), person = PERSON_BODY_HEIGHT * ps.k;
  const dig = planet.digs.find(g => g.done >= 0.5 && arcOn(g.dir, p, R) <= 0.5 * person);
  if (!dig) return null;
  const t = vecNormalize(vecSub(threat, planet.position));
  if (arcOn(t, p, R) > rifleEffectivePeople() * person) return null;
  return vecDot(vecSub(t, vecScale(p, vecDot(t, p))), dig.facing) > 0 ? dig : null;
}

/**
 * 66.7 (the owner: *"I want it to be aggressive cover where they're trying to minimize exposure to enemy while dealing damage
 * to attackers. I think that's a tree lean or rock lean as optimal"*): the cover pose a holder takes at its post against its
 * threat (a world point) — at a trunk, `corner` (standing, leaning round it); at a rock or a wall, `edge` (kneeling, leaning
 * round it), or `low` where it is lower than a person rising over it; on the ground, `crest` behind a lip it lies at, `low`
 * crouched in a fold — with the side it leans to. Null: no prop at hand and nothing the ground gives (it stands as before).
 * Chosen with the posts (a post decision).
 */
export function postPose(state: Pick<GameState, 'planets' | 'rocks' | 'flora' | 'structures' | 'config'>, planetIndex: number, post: Vec3, threat: Vec3, stance: 'crouch' | 'stand' | 'prone' | 'rise', ps: PersonSet, obstacles: readonly WalkObstacle[]): { pose: CoverPose; side?: 1 | -1; step?: number; at?: Vec3 } | null {
  const planet = state.planets[planetIndex];
  const R = planet.radius, d = vecNormalize(post), person = PERSON_BODY_HEIGHT * ps.k;
  const tDir = vecNormalize(vecSub(threat, planet.position));
  // 66.8: in a dig half dug or more, the dig's own pose (digCover: a scrape a crest, a foxhole low) — its ground is the cover
  for (const dg of planet.digs ?? []) {
    if (dg.done < 0.5 || arcOn(dg.dir, d, R) > 0.5 * person) continue;
    const cov = digCover(dg.kind, dg.material, dg.done);
    if (cov) return { pose: cov.pose };
  }
  // the prop it stands at: the nearest whose clearance it is just outside
  let prop: WalkObstacle | null = null, gap = Infinity;
  for (const o of obstacles) {
    const g = arcOn(d, o.dir, R) - o.radius;
    if (g < gap && g < 1.5 * ps.unitRadius + 0.3 * ps.k) { gap = g; prop = o; }
  }
  if (prop) {
    const low = prop.height < (COVER_POSE_SIZE.low.maxEye - COVER_POSE_SIZE.low.clear) * person;
    if (low) return { pose: 'low' };
    // (70.3: a trunk that hides no standing body across it — a thin one — is knelt at, its lean round it the edge's)
    const thinTrunk = prop.ref?.kind === 'flora' && COVER_ACROSS.kneelAtTrunks && coverAcrossOn(state.config)
      && shelter(state, planetIndex, d, personPointOn(planet, tDir, ps, ps.losEyeHeight), 'stand').kind !== 'covered';
    const pose: 'corner' | 'edge' = prop.ref?.kind === 'flora' && !thinTrunk ? 'corner' : 'edge';
    // (the lean: in place where the pose's reach clears — the body at the post — else the smallest step aside that does)
    const lean = leanSide(state, planet, d, tDir, ps, obstacles, leanReachPeople(pose));
    return { pose, ...(lean ? { side: lean.side, step: lean.step, ...(lean.at ? { at: lean.at } : {}) } : {}) };
  }
  if (stance === 'prone') return { pose: 'crest' };
  if (stance === 'crouch' || stance === 'rise') return { pose: 'low' };
  return null;
}

/**
 * 66.7: a holder's posture at its post, decided with the posts — the stance (postStance, by the gun's line) and the cover pose
 * (postPose); at a prop the pose sets the stance: a corner stands, an edge kneels, low cover rises to fire, a crest lies.
 */
export function postHold(state: Pick<GameState, 'planets' | 'rocks' | 'flora' | 'structures' | 'config'>, planetIndex: number, post: Vec3, threat: Vec3, ps: PersonSet, obstacles: readonly WalkObstacle[]): { stance: 'crouch' | 'stand' | 'prone' | 'rise'; pose?: { pose: CoverPose; side?: 1 | -1; step?: number }; at?: Vec3 } {
  const stance = postStance(state, planetIndex, post, threat, ps);
  const pose = postPose(state, planetIndex, post, threat, stance, ps, obstacles);
  if (!pose) return { stance };
  const byPose = pose.pose === 'corner' ? 'stand' : pose.pose === 'edge' ? 'crouch' : pose.pose === 'low' ? 'rise' : 'prone';
  // (a lean post moved to its cover's edge: the post the holder takes — lean step 2)
  const { at, ...rest } = pose;
  return { stance: byPose, pose: rest, ...(at ? { at } : {}) };
}

/**
 * 66.11 (the owner: "Bots don't really seem to use it"; sweeps/2026-10-12-cover-use.md — a healthy bot shot at had no rule
 * to take cover): the spot a bot under fire makes for. The cover points (core/coverMap.ts) and the wide rocks' slots
 * (core/coverSlots.ts) within `reach` (world units along the ground) of `from` — a point only where its bearing table says it
 * shelters toward the shooter — and the ground's own (F2's reverse slopes and dead ground, `terrainSpotsNear` against the
 * shooter's eye: on open ground the commonest cover, sweeps/2026-10-12-cover-use.md), tried nearest first, at most `tries` of
 * them, the first whose spot hides a crouched body from the shooter's eye — a ground spot that hides only one lying down,
 * a prone body — (`shelter`, exactly: a prop or the ground stops the round) and where no teammate stands (`mates`, within a
 * body). Null: nothing in reach shelters from that shooter.
 */
export function chooseFireCover(args: {
  state: Pick<GameState, 'planets' | 'config' | 'rocks' | 'flora' | 'structures'> & Partial<Pick<GameState, 'ruins' | 'terrainEdits' | 'groundWear' | 'smokes'>>;
  planetIndex: number;
  from: Vec3;
  /** the shooter's place (a unit surface direction) */
  threat: Vec3;
  reach: number;
  ps: PersonSet;
  mates: readonly Vec3[];
  tries?: number;
}): Vec3 | null {
  const { state, planetIndex, ps, reach } = args;
  const planet = state.planets[planetIndex];
  if (!planet) return null;
  const R = planet.radius, n = vecNormalize(args.from);
  const eye = personPointOn(planet, vecNormalize(args.threat), ps, ps.losEyeHeight);
  const cands: Array<{ dir: Vec3; d: number; stance: 'crouch' | 'prone' }> = [];
  for (const p of coverPointsNear(state, planetIndex, n, reach)) if (!p.concealment && p.shelter[coverBearing(p, eye)] !== null) cands.push({ dir: vecNormalize(p.dir), d: arcOn(p.dir, n, R), stance: 'crouch' });
  for (const sl of coverSlotsNear(state, planetIndex, n, reach)) cands.push({ dir: vecNormalize(sl.dir), d: arcOn(sl.dir, n, R), stance: 'crouch' });
  // (66.11, the ground: a reverse slope or dead ground hiding at least a body lying down — a crest is a step to fire from)
  for (const g of terrainSpotsNear(state, planetIndex, n, reach, eye)) if (g.hides !== null && g.kind !== 'crest') cands.push({ dir: vecNormalize(g.dir), d: arcOn(g.dir, n, R), stance: g.hides === 'prone' ? 'prone' : 'crouch' });
  cands.sort((a, b) => a.d - b.d);
  const body = ps.unitRadius * 2;
  let tried = 0;
  for (const c of cands) {
    if (args.mates.some(m => arcOn(vecNormalize(m), c.dir, R) < body) || collapseHazardAt(state, planetIndex, c.dir)) continue;   // (72.3 step 7)
    if (tried++ >= (args.tries ?? 6)) break;
    if (shelter(state, planetIndex, c.dir, eye, c.stance).kind === 'covered') return c.dir;
  }
  return null;
}
